import { getBoundariesByCountryCode } from "@/util/http/ClimateShift";
import { IFeatureCollectionModel } from "@/util/http/Lightning/responseType";
import { Spin } from "antd";
import { geoPath, geoAlbersUsa, geoMercator, geoIdentity } from "d3-geo";
import { FeatureCollection, Geometry } from "geojson";
import React, { useEffect, useRef, useState } from "react";
import { climateShiftStore } from "@/util/localforageUtils";
import { USDATA } from "./US";

// 1. 定义你的 SVG 字符串
// 为了方便在地图上看到，我手动添加了 fill="#d9363e" (红色)，你可以根据需要修改颜色
const MARKER_SVG = `
<svg width="45" height="45" viewBox="0 0 45 45" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M22 0C22.5523 0 23 0.447715 23 1V6.03027C31.5924 6.52873 38.4713 13.4076 38.9697 22H44C44.5523 22 45 22.4477 45 23C45 23.5523 44.5523 24 44 24H38.9697C38.4713 32.5924 31.5924 39.4703 23 39.9688V44C23 44.5523 22.5523 45 22 45C21.4477 45 21 44.5523 21 44V39.9688C12.4076 39.4703 5.52873 32.5924 5.03027 24H1C0.447715 24 0 23.5523 0 23C0 22.4477 0.447715 22 1 22H5.03027C5.52873 13.4076 12.4076 6.52873 21 6.03027V1C21 0.447715 21.4477 0 22 0ZM7.03418 24C7.52705 31.4874 13.5126 37.4729 21 37.9658V33.9521C15.723 33.4765 11.5225 29.277 11.0469 24H7.03418ZM32.9531 24C32.4775 29.277 28.277 33.4765 23 33.9521V37.9658C30.4874 37.4729 36.4729 31.4874 36.9658 24H32.9531ZM22 14C17.3675 14 13.5542 17.5001 13.0566 22H13.0576C13.0213 22.3284 13 22.6619 13 23C13 23.3381 13.0213 23.6716 13.0576 24H13.0566C13.5179 28.1714 16.8286 31.4811 21 31.9424C21.3284 31.9787 21.6619 32 22 32C22.3381 32 22.6716 31.9787 23 31.9424C27.1714 31.4811 30.4821 28.1714 30.9434 24C30.9797 23.6716 31 23.3381 31 23C31 22.6619 30.9797 22.3284 30.9434 22C30.4458 17.5001 26.6325 14 22 14ZM23 12.0469C28.277 12.5225 32.4775 16.723 32.9531 22H36.9658C36.4729 14.5125 30.4875 8.52604 23 8.0332V12.0469ZM21 8.0332C13.5125 8.52604 7.52705 14.5125 7.03418 22H11.0469C11.5225 16.723 15.723 12.5225 21 12.0469V8.0332Z" fill="#FF0000" stroke="#FF0000" stroke-width="2"/>
<circle cx="22" cy="22.5" r="2.25" stroke="#FF0000" stroke-width="6"/>
</svg>
`;

// 2. 定义标记点数据 (经纬度)
const LOCATIONSA = [
    { name: "Rome", coordinates: [12.4964, 41.9028] }, // 罗马
    { name: "Milan", coordinates: [9.1900, 45.4642] }, // 米兰
    { name: "Naples", coordinates: [14.2681, 40.8518] }, // 那不勒斯
    { name: "Palermo", coordinates: [13.3615, 38.1157] }, // 巴勒莫 (西西里岛)
];

interface AbbreviatedMapProps {
    countryCode: string;
    lat: number;
    lng: number;
}
const AbbreviatedMap = (prop: AbbreviatedMapProps) => {

    const { countryCode, lat, lng } = prop;
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [boundariesData, setBoundariesData] = useState<IFeatureCollectionModel | null>(null);
    const [getBoundaryDataLoading, setGetBoundaryDataLoading] = useState<boolean>(false);
    const [renderLoading, setRenderLoading] = useState<boolean>(false);
    const [loadingTitle, setLoadingTitle] = useState<string>("");




    const getBoundariesByCountryCodeFunc = async (countryCode: string) => {
        try {
            setGetBoundaryDataLoading(true);
            setLoadingTitle("Getting boundary data...");
            const res = await getBoundariesByCountryCode(countryCode);
            if (res && res.data) {
                setBoundariesData(res.data);
                // 存储到 localforage
                await climateShiftStore.setItem<string>(countryCode, JSON.stringify(res.data));
            }
        }
        catch (err) {
            console.error("Error fetching boundaries:", err);
        }
        finally {
            setGetBoundaryDataLoading(false);
            setLoadingTitle("");
        }
    };

    const boundaryDataSettinnAsync = async () => {
        // if (countryCode && countryCode === "US") {
        //     setBoundariesData(USDATA as IFeatureCollectionModel);
        //     return;
        // }
        if (countryCode) {
            const tmpCountryBoundary = await climateShiftStore.getItem<string>(countryCode);
            if (tmpCountryBoundary) {
                const tmpData = JSON.parse(tmpCountryBoundary) as IFeatureCollectionModel;
                setBoundariesData(tmpData);
            }
            else {
                getBoundariesByCountryCodeFunc(countryCode);
            }
        }
    };
    useEffect(() => {
        boundaryDataSettinnAsync();
    }, [countryCode]);


    // 将这个函数保留，因为它用于将自定义 GeoJSON 格式转换为标准的 GeoJSON 格式
    function toGeoJSON(fc: IFeatureCollectionModel): FeatureCollection<Geometry> {
        return {
            type: "FeatureCollection",
            features: (fc.features || []).map(f => ({
                type: "Feature",
                geometry: f.geometry as Geometry,
                properties: f.properties || {},
            })),
        };
    }

    useEffect(() => {
        if (!boundariesData) {
            return;
        }
        setRenderLoading(true);
        setLoadingTitle("Rendering abbreviated map...");
        const canvas = canvasRef.current!;
        const ctx = canvas.getContext("2d")!;

        // --- 数据准备 (同上一次修正) ---
        const data = JSON.parse(JSON.stringify(boundariesData)) as IFeatureCollectionModel;
        const geoJsonFC: FeatureCollection<Geometry> = {
            type: "FeatureCollection",
            features: (data.features || []).map(f => ({
                type: "Feature",
                geometry: f.geometry as Geometry,
                properties: f.properties || {},
            })),
        };

        // 修正绕行方向
        geoJsonFC.features.forEach(feature => {
            const geometry = feature.geometry;
            if (geometry.type === "MultiPolygon") {
                geometry.coordinates.forEach(polygon => polygon.forEach(ring => ring.reverse()));
            } else if (geometry.type === "Polygon") {
                geometry.coordinates.forEach(ring => ring.reverse());
            }
        });

        // --- 投影设置 ---
        const width = canvas.width;
        const height = canvas.height;
        let projection = null;

        if (countryCode === "US") {
            projection = geoAlbersUsa().scale(300).translate([width / 2, height / 2]);
        }
        else {
            projection = geoIdentity()
                .reflectY(true) // 因为 Canvas Y轴是向下的，经度是向上的，所以要翻转
                .fitExtent([[0, 0], [width, height]], geoJsonFC);
            // .fitExtent([[20, 20], [width - 20, height - 20]], geoJsonFC);
        }

        const path = geoPath().projection(projection).context(ctx);

        const LOCATIONS = [
            {
                name: "",
                coordinates: [lng, lat]
            }
        ];

        // --- 绘制函数 ---
        const draw = () => {
            // 1. 清空画布
            ctx.clearRect(0, 0, width, height);

            // 2. 绘制地图背景
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, width, height);

            // 3. 绘制地图实体
            ctx.beginPath();
            path(geoJsonFC);
            ctx.fillStyle = "black";
            ctx.fill();
            ctx.lineWidth = 1;
            ctx.strokeStyle = "#999";
            ctx.stroke();

            // --- 核心步骤：绘制 Marker ---

            // 创建 Image 对象
            const img = new Image();
            // 将 SVG 字符串转为 Base64 格式的 Data URI
            // encodeURIComponent 确保 SVG 中的特殊字符不会破坏 URL 格式
            img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(MARKER_SVG);

            // 图片加载完成后绘制
            img.onload = () => {
                const markerSize = 24; // 设置 Marker 在地图上的显示大小 (px)

                LOCATIONS.forEach(loc => {
                    // projection 接收 [经度, 纬度]，返回 [x, y]
                    // 注意：D3 的坐标顺序是 [经度, 纬度]，而 Google Maps 常是 [纬度, 经度]，别搞反了
                    const projected = projection(loc.coordinates as [number, number]);

                    if (projected) {
                        const [x, y] = projected;

                        // drawImage 参数: image, x, y, width, height
                        // 我们需要减去 markerSize / 2，让图标的中心点对准坐标点
                        ctx.drawImage(
                            img,
                            x - markerSize / 2,
                            y - markerSize / 2,
                            markerSize,
                            markerSize
                        );

                        // (可选) 绘制文字标签
                        ctx.fillStyle = "#333";
                        ctx.font = "12px Arial";
                        ctx.fillText(loc.name, x + markerSize / 2 + 2, y + 4);
                    }
                });
            };
        };

        draw();
        setRenderLoading(false);
        setLoadingTitle("");

    }, [boundariesData, lat, lng]);


    return (
        <div>
            <Spin spinning={getBoundaryDataLoading || renderLoading} tip={loadingTitle}>
                <canvas ref={canvasRef} style={{ width: "246px", height: "auto" }}
                // style={{ border: "1px solid #eee" }}
                />
            </Spin>

        </div>
    );
};

// export default React.memo(AbbreviatedMap);
export default AbbreviatedMap;